import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { chatbotUrls, formatMessageResponse } from "./chatbotUtils";

export const startConversation = createAsyncThunk(
  "chatbot/startConversation",
  async ({ question, queryType = '' }, thunkAPI) => {
    const data = JSON.parse(localStorage.getItem("data"));
    const token = data?.token;
    const email = data?.email || "";
    
    // Extract and join country codes
    const countries = data?.userCountries
      ?.map(item => {
        const name = item.country?.countryName;
        const code = item.country?.countryCode;
        return name && code ? `${name}(${code})` : null;
      })
      .filter(entry => entry) // Remove null/undefined values
      .join(', ') || "";

    const requestBody = {
      question,
      email,
      country: countries,
      queryType: queryType || ''
    };
    
    try {
      const response = await fetch(chatbotUrls.startConversation, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
          Accept: "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start conversation');
      }
      
      const jsonData = await response.json();
      return jsonData;
    } catch (error) {
      console.error("Error in chatbot/startConversation : ", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const continueChat = createAsyncThunk(
  "chatbot/continueChat",
  async ({ conversationId, question, chatId, queryType = '', isGenieConversation = false }, thunkAPI) => {
    const data = JSON.parse(localStorage.getItem("data"));
    const token = data?.token;
    const email = data?.email || "";

        // Extract and join country codes
        const countries = data?.userCountries
        ?.map(item => {
          const name = item.country?.countryName;
          const code = item.country?.countryCode;
          return name && code ? `${name}(${code})` : null;
        })
        .filter(entry => entry) // Remove null/undefined values
        .join(', ') || "";
    
    const requestBody = {
      conversationId,
      question,
      email,
      country: countries,
      chatId: chatId || 0,
      queryType: queryType || '',
      isGenieConversation
    };
    
    console.log('continueChat - requestBody:', requestBody);
    
    try {
      const response = await fetch(chatbotUrls.continueChat, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
          Accept: "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to continue conversation');
      }
      
      const jsonData = await response.json();
      return jsonData;
    } catch (error) {
      console.error("Error in chatbot/continueChat : ", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getConversationHistory = createAsyncThunk(
  "chatbot/getConversationHistory",
  async ({ chatId }, thunkAPI) => {
    const data = JSON.parse(localStorage.getItem("data"));
    const token = data?.token;
    const url = chatbotUrls.conversationHistory + `?chatId=${chatId}`;
    
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
          Accept: "application/json",
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch conversation history');
      }
      
      const jsonData = await response.json();
      return jsonData;
    } catch (error) {
      console.error("Error in chatbot/getConversationHistory : ", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getFollowupQuestions = createAsyncThunk(
  "chatbot/getFollowupQuestions",
  async ({ userQuestion, aiResponse, conversationId }, thunkAPI) => {
    const data = JSON.parse(localStorage.getItem("data"));
    const token = data?.token;
    
    const requestBody = {
      userQuestion,
      aiResponse,
      conversationId: conversationId || ''
    };
    
    try {
      const response = await fetch(chatbotUrls.followupQuestions, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
          Accept: "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch followup questions');
      }
      
      const jsonData = await response.json();
      return jsonData;
    } catch (error) {
      console.error("Error in chatbot/getFollowupQuestions : ", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getMostAskedQuestions = createAsyncThunk(
  "chatbot/getMostAskedQuestions",
  async (_, thunkAPI) => {
    const data = JSON.parse(localStorage.getItem("data"));
    const token = data?.token;
    const email = data?.email || "";
    
    // Extract and join country codes
    const countries = data?.userCountries
      ?.map(item => {
        const name = item.country?.countryName;
        const code = item.country?.countryCode;
        return name && code ? `${name}(${code})` : null;
      })
      .filter(entry => entry) // Remove null/undefined values
      .join(', ') || "";

    const url = chatbotUrls.mostAskedQuestions + 
      `?email=${encodeURIComponent(email)}&countryCodes=${encodeURIComponent(countries)}`;
    
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
          Accept: "application/json",
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch most asked questions');
      }
      
      const jsonData = await response.json();
      return jsonData.questions || [];
    } catch (error) {
      console.error("Error in chatbot/getMostAskedQuestions : ", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getFrequentlyAskedQuestions = createAsyncThunk(
  "chatbot/getFrequentlyAskedQuestions",
  async (_, thunkAPI) => {
    const data = JSON.parse(localStorage.getItem("data"));
    const token = data?.token;
    const email = data?.email || "";
    
    // Extract country code (single country, not multiple)
    const country = data?.userCountries?.[0]?.country?.countryCode || "";
    
    const url = chatbotUrls.frequentlyAskedQuestions + 
      `?email=${encodeURIComponent(email)}&country=${encodeURIComponent(country)}`;
    
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
          Accept: "application/json",
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch frequently asked questions');
      }
      
      const jsonData = await response.json();
      return jsonData.questions || [];
    } catch (error) {
      console.error("Error in chatbot/getFrequentlyAskedQuestions : ", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getDashboardCards = createAsyncThunk(
  "chatbot/getDashboardCards",
  async (_, thunkAPI) => {
    console.log("🚀 getDashboardCards thunk started");
    
    const data = JSON.parse(localStorage.getItem("data"));
    console.log("📦 LocalStorage data:", data ? "Found" : "Not found");
    
    const token = data?.token;
    const email = data?.email || "";
    
    // Extract and join country codes in format "CountryName(Code)"
    const countries = data?.userCountries
      ?.map(item => {
        const name = item.country?.countryName;
        const code = item.country?.countryCode;
        return name && code ? `${name}(${code})` : null;
      })
      .filter(entry => entry) // Remove null/undefined values
      .join(', ') || "";
    
    console.log("🌍 Countries:", countries || "None");
    console.log("📧 Email:", email || "None");
    
    const url = chatbotUrls.dashboardCards + 
      `?countryCodes=${encodeURIComponent(countries)}&email=${encodeURIComponent(email)}`;
    
    console.log("🔄 Fetching dashboard cards from:", url);
    console.log("⏳ Waiting for backend (may take 30-120 seconds for retries and fallback)...");
    
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
          Accept: "application/json",
        },
      });
      
      console.log("📡 Response received from server, status:", response.status);
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // Response wasn't JSON
        }
        throw new Error(errorMessage);
      }
      
      const jsonData = await response.json();
      console.log("✅ Dashboard cards API response received:", jsonData);
      console.log("📊 Keys in response:", Object.keys(jsonData || {}));
      return jsonData;
    } catch (error) {
      console.error("❌ Error in getDashboardCards:", error.message);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getUserChatBotHistory = createAsyncThunk(
  "chatbot/getUserChatBotHistory",
  async (_, thunkAPI) => {
    const data = JSON.parse(localStorage.getItem("data"));
    const token = data?.token;
    const userId = data?.userID || data?.user?.userID || data?.userId || data?.user?.userId;
    
    if (!userId) {
      console.error("UserID not found in localStorage data");
      return thunkAPI.rejectWithValue("UserID not found");
    }
    
    const url = chatbotUrls.getUserChatBotHistory + `?UserId=${userId}`;
    
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
          Accept: "application/json",
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch user chat bot history');
      }
      
      const jsonData = await response.json();
      return jsonData;
    } catch (error) {
      console.error("Error in chatbot/getUserChatBotHistory : ", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const initialState = {
  isLoading: false,
  isSuccess: false,
  isSuccess_Info: "",
  isFailed: false,
  isFailed_Info: "",
  isError: false,
  errorMessage: "",
  messages: [],
  chatHistory: [
    { 
      id: 'new-chat', 
      title: 'New Chat', 
      timestamp: new Date(), 
      conversationId: null 
    }
  ],
  currentChatId: 'new-chat',
  currentConversationId: null,
  isGenieConversation: false, // Track if currentConversationId is a Genie conversation
  currentDatabaseChatId: null, // Database ChatID from ChatBot_Chats table
  currentQuestion: "",
  lastResponse: null,
  followupQuestions: [], // Followup questions (not part of conversation history)
  isLoadingFollowupQuestions: false,
  dashboardCards: {
    // ROW 1 - 8 KPI Cards
    adherenceToProductObjectives: { title: '', chartType: 'horizontalBar', data: [] },
    quotasAtRisk: { title: '', chartType: 'verticalBar', data: [] },
    quotasPendingSubmission: { title: '', chartType: 'horizontalBar', data: [] },
    quotasPendingApproval: { title: '', chartType: 'horizontalBar', data: [] },
    // ROW 2
    topCountriesHighestDemand: { title: '', chartType: 'horizontalBar', data: [] },
    topCountriesMostExcess: { title: '', chartType: 'horizontalBar', data: [] },
    topQuotaBreachesVsInitial: { title: '', chartType: 'horizontalBar', data: [] },
    topProductExcessGrowth: { title: '', chartType: 'horizontalBar', data: [] },
  },
  isLoadingDashboardCards: true, // Start as true to show skeletons until data arrives
  dashboardCardsError: null, // Error message if API fails
  mostAskedQuestions: [], // Most asked questions (replaces GenAIConfig.suggestedPrompts)
  isLoadingMostAskedQuestions: false,
};

const chatbotSlice = createSlice({
  name: "chatbot",
  initialState,
  reducers: {
    setState: {
      prepare(stateName, stateValue) {
        return {
          payload: { stateName, stateValue },
        };
      },
      reducer(state, action) {
        const { stateName, stateValue } = action.payload;
        state[stateName] = stateValue;
      },
    },
    addUserMessage: (state, action) => {
      const userMessage = {
        id: Date.now(),
        text: action.payload.text,
        sender: 'user',
        timestamp: new Date().toISOString(),
      };
      state.messages.push(userMessage);
      state.currentQuestion = action.payload.text;
    },
    addBotMessage: (state, action) => {
      const botMessage = {
        id: Date.now() + 1,
        sender: 'bot',
        timestamp: new Date().toISOString(),
        description: action.payload.description,
        results: action.payload.results || [],
        totalAttachments: action.payload.totalAttachments || 0,
      };
      state.messages.push(botMessage);
    },
    addErrorMessage: (state, action) => {
      const errorMessage = {
        id: Date.now() + 1,
        sender: 'bot',
        timestamp: new Date().toISOString(),
        text: action.payload.text || 'An error occurred',
        isError: true,
      };
      state.messages.push(errorMessage);
    },
    clearMessages: (state) => {
      state.messages = [];
      state.currentConversationId = null;
      state.isGenieConversation = false;
      state.currentDatabaseChatId = null;
      state.currentQuestion = "";
      state.errorMessage = "";
      state.lastResponse = null;
    },
    setCurrentChat: (state, action) => {
      state.currentChatId = action.payload.chatId;
      state.currentConversationId = action.payload.conversationId;
      state.isGenieConversation = action.payload.isGenieConversation || false;
      // Set database ChatID if provided (from history), otherwise clear it
      state.currentDatabaseChatId = action.payload.databaseChatId || null;
      state.messages = [];
      state.errorMessage = "";
    },
    addNewChat: (state, action) => {
      const newChat = {
        id: action.payload.id || `chat-${Date.now()}`,
        title: action.payload.title || 'New Chat',
        timestamp: new Date().toISOString(),
        conversationId: action.payload.conversationId || null,
        isGenieConversation: false,
      };
      state.chatHistory.unshift(newChat);
      state.currentChatId = newChat.id;
      state.currentConversationId = newChat.conversationId;
      state.isGenieConversation = false;
      state.currentDatabaseChatId = null; // Clear database ChatID for new chat
      state.messages = [];
    },
    updateChatTitle: (state, action) => {
      const { chatId, title, conversationId } = action.payload;
      const chatIndex = state.chatHistory.findIndex(chat => chat.id === chatId);
      if (chatIndex !== -1) {
        state.chatHistory[chatIndex].title = title;
        if (conversationId) {
          state.chatHistory[chatIndex].conversationId = conversationId;
        }
      }
    },
    deleteChat: (state, action) => {
      state.chatHistory = state.chatHistory.filter(
        chat => chat.id !== action.payload.chatId
      );
    },
    clearError: (state) => {
      state.isError = false;
      state.errorMessage = "";
      state.isFailed = false;
      state.isFailed_Info = "";
    },
    clearFollowupQuestions: (state) => {
      state.followupQuestions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startConversation.pending, (state, action) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(startConversation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        
        // Handle conversationId based on query type
        const responseType = action.payload.type?.toUpperCase();
        
        console.log('startConversation.fulfilled - Response type:', responseType);
        console.log('startConversation.fulfilled - Response payload:', action.payload);
        
        // Always save the conversationId for continuing the conversation
        // But track whether it's a Genie conversation or not
        if (responseType === 'DATA_QUERY') {
          // DATA_QUERY: Save the conversationId (it's a Genie conversationId)
          state.currentConversationId = action.payload.conversationId;
          state.isGenieConversation = true;
          console.log('DATA_QUERY - Saving Genie conversationId:', action.payload.conversationId);
        } else if (responseType === 'BUSINESS_QUERY') {
          // BUSINESS_QUERY: Save the conversationId but mark as NOT a Genie conversation
          // This allows continueChat to know it needs to create a new Genie conversation for data queries
          state.currentConversationId = action.payload.conversationId;
          state.isGenieConversation = false;
          console.log('BUSINESS_QUERY - Saving business conversationId (not Genie):', action.payload.conversationId);
        } else if (responseType === 'COMPOSITE') {
          // COMPOSITE: Extract Genie conversationId from dataResult
          const genieConversationId = action.payload.dataResult?.conversationId || action.payload.conversationId;
          state.currentConversationId = genieConversationId;
          state.isGenieConversation = true;
          console.log('COMPOSITE - Saving Genie conversationId from dataResult:', genieConversationId);
        } else {
          // Default: treat as DATA_QUERY
          state.currentConversationId = action.payload.conversationId;
          state.isGenieConversation = true;
          console.log('DEFAULT - Saving conversationId as Genie:', action.payload.conversationId);
        }
        
        // Save ChatID from database if returned
        if (action.payload.chatId) {
          state.currentDatabaseChatId = action.payload.chatId;
        }
        state.lastResponse = formatMessageResponse(action.payload);
        
        // Add bot message to messages
        const botMessage = {
          id: Date.now() + 1,
          sender: 'bot',
          timestamp: new Date().toISOString(),
          description: action.payload.description || action.payload.message,
          results: action.payload.results || action.payload.dataResult?.results || [],
          totalAttachments: action.payload.totalAttachments || action.payload.dataResult?.totalAttachments || 0,
          queryType: responseType,
          businessQuestion: action.payload.businessQuestion || null,
        };
        state.messages.push(botMessage);
        
        // Update chat history with the conversation ID, ChatID, and title
        const currentChatIndex = state.chatHistory.findIndex(
          chat => chat.id === state.currentChatId
        );
        if (currentChatIndex !== -1 && state.currentQuestion) {
          const updatedChat = {
            ...state.chatHistory[currentChatIndex],
            conversationId: state.currentConversationId,
            isGenieConversation: state.isGenieConversation,
            chatId: action.payload.chatId || null,
            title: state.currentQuestion.substring(0, 30) + 
                   (state.currentQuestion.length > 30 ? '...' : ''),
            timestamp: new Date().toISOString(),
          };
          
          // Remove the chat from its current position
          state.chatHistory.splice(currentChatIndex, 1);
          
          // Find "New Chat" index and insert after it (or at top if not found)
          const newChatIndex = state.chatHistory.findIndex(chat => chat.id === 'new-chat');
          if (newChatIndex !== -1) {
            state.chatHistory.splice(newChatIndex + 1, 0, updatedChat);
          } else {
            // If no "New Chat", put at top
            state.chatHistory.unshift(updatedChat);
          }
        }
      })
      .addCase(startConversation.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isFailed = true;
        state.errorMessage = action.payload || 'Failed to start conversation';
        state.isFailed_Info = action.payload || 'Failed to start conversation';
        console.error("Error from startConversation", action.error?.message);
      })
      .addCase(continueChat.pending, (state, action) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(continueChat.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        
        // Handle conversationId based on query type
        const responseType = action.payload.type?.toUpperCase();
        
        console.log('continueChat.fulfilled - Response type:', responseType);
        console.log('continueChat.fulfilled - Response payload:', action.payload);
        console.log('continueChat.fulfilled - Existing conversationId:', state.currentConversationId);
        console.log('continueChat.fulfilled - Existing isGenieConversation:', state.isGenieConversation);
        
        if (responseType === 'DATA_QUERY') {
          // DATA_QUERY: Update conversationId if returned (it's a Genie conversationId)
          // This could be a new Genie conversation created by the API if previous was not Genie
          if (action.payload.conversationId) {
            state.currentConversationId = action.payload.conversationId;
            state.isGenieConversation = true;
            console.log('DATA_QUERY - Updated to Genie conversationId:', action.payload.conversationId);
          }
        } else if (responseType === 'BUSINESS_QUERY') {
          // BUSINESS_QUERY: Keep existing conversationId and isGenieConversation flag
          console.log('BUSINESS_QUERY - Keeping existing state, isGenieConversation:', state.isGenieConversation);
        } else if (responseType === 'COMPOSITE') {
          // COMPOSITE: Extract Genie conversationId from dataResult
          const newGenieId = action.payload.dataResult?.conversationId || action.payload.conversationId;
          if (newGenieId) {
            state.currentConversationId = newGenieId;
            state.isGenieConversation = true;
            console.log('COMPOSITE - Updated to Genie conversationId from dataResult:', newGenieId);
          }
        } else {
          // Default: update conversationId if returned and mark as Genie
          if (action.payload.conversationId) {
            state.currentConversationId = action.payload.conversationId;
            state.isGenieConversation = true;
            console.log('DEFAULT - Updated conversationId:', action.payload.conversationId);
          }
        }
        
        // Save ChatID from database if returned (may be same or new)
        if (action.payload.chatId) {
          state.currentDatabaseChatId = action.payload.chatId;
          
          // Update the chat history item with the ChatID, conversationId, and move it to top (if it exists)
          const currentChatIndex = state.chatHistory.findIndex(
            chat => chat.id === state.currentChatId || chat.conversationId === state.currentConversationId
          );
          if (currentChatIndex !== -1) {
            const updatedChat = {
              ...state.chatHistory[currentChatIndex],
              chatId: action.payload.chatId,
              conversationId: state.currentConversationId,
              isGenieConversation: state.isGenieConversation,
              timestamp: new Date().toISOString(),
            };
            
            // Remove from current position and add to top (after "New Chat")
            state.chatHistory.splice(currentChatIndex, 1);
            // Find "New Chat" index
            const newChatIndex = state.chatHistory.findIndex(chat => chat.id === 'new-chat');
            if (newChatIndex !== -1) {
              state.chatHistory.splice(newChatIndex + 1, 0, updatedChat);
            } else {
              state.chatHistory.unshift(updatedChat);
            }
          }
        }
        state.lastResponse = formatMessageResponse(action.payload);
        
        // Add bot message to messages
        const botMessage = {
          id: Date.now() + 1,
          sender: 'bot',
          timestamp: new Date().toISOString(),
          description: action.payload.description || action.payload.message,
          results: action.payload.results || action.payload.dataResult?.results || [],
          totalAttachments: action.payload.totalAttachments || action.payload.dataResult?.totalAttachments || 0,
          queryType: responseType,
          businessQuestion: action.payload.businessQuestion || null,
        };
        state.messages.push(botMessage);
      })
      .addCase(continueChat.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isFailed = true;
        state.errorMessage = action.payload || 'Failed to continue chat';
        state.isFailed_Info = action.payload || 'Failed to continue chat';
        console.error("Error from continueChat", action.error?.message);
      })
      .addCase(getConversationHistory.pending, (state, action) => {
        state.isLoading = true;
        state.messages = []; // Clear messages while loading
      })
      .addCase(getConversationHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        
        // Transform API response messages into UI message format
        if (action.payload && action.payload.messages && Array.isArray(action.payload.messages)) {
          const transformedMessages = [];
          let messageIdCounter = 1;
          
          action.payload.messages.forEach((msg) => {
            // Add user message
            if (msg.userQuestion) {
              transformedMessages.push({
                id: messageIdCounter++,
                sender: 'user',
                text: msg.userQuestion,
                timestamp: msg.createdTimestamp || new Date().toISOString(),
              });
            }
            
            // Add bot message
            if (msg.aiResponse || (msg.results && msg.results.length > 0)) {
              transformedMessages.push({
                id: messageIdCounter++,
                sender: 'bot',
                timestamp: msg.lastUpdatedTimestamp || msg.createdTimestamp || new Date().toISOString(),
                description: msg.aiResponse || '',
                results: msg.results || [],
                totalAttachments: msg.results ? msg.results.length : 0,
                type: msg.type || '', // Include type for BUSINESS_QUERY check
              });
            }
          });
          
          state.messages = transformedMessages;
          
          // Update current conversation ID if available
          if (action.payload.conversationId) {
            state.currentConversationId = action.payload.conversationId;
          }
        }
      })
      .addCase(getConversationHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload || 'Failed to fetch conversation history';
        console.error("Error from getConversationHistory", action.error?.message);
      })
      .addCase(getFollowupQuestions.pending, (state, action) => {
        state.isLoadingFollowupQuestions = true;
      })
      .addCase(getFollowupQuestions.fulfilled, (state, action) => {
        state.isLoadingFollowupQuestions = false;
        state.followupQuestions = action.payload.followupQuestions || [];
      })
      .addCase(getFollowupQuestions.rejected, (state, action) => {
        state.isLoadingFollowupQuestions = false;
        state.followupQuestions = [];
        console.error("Error from getFollowupQuestions", action.error?.message);
      })
      .addCase(getDashboardCards.pending, (state, action) => {
        state.isLoadingDashboardCards = true;
        state.dashboardCardsError = null; // Clear previous error
        console.log("⏳ [REDUX] getDashboardCards.pending - Loading started");
      })
      .addCase(getDashboardCards.fulfilled, (state, action) => {
        console.log("✅ [REDUX] getDashboardCards.fulfilled - Response received");
        console.log("📦 [REDUX] Payload:", action.payload);
        
        // Map API response to the 8 KPI cards structure
        // Backend always returns data (either real or fallback with HTTP 200)
        const payload = action.payload || {};
        
        // Helper to safely extract widget data from API response
        const extractWidgetData = (widgetKey) => {
          const widget = payload[widgetKey];
          if (widget && widget.data && Array.isArray(widget.data) && widget.data.length > 0) {
            console.log(`  ✅ ${widgetKey}: ${widget.data.length} items`);
            return {
              title: widget.title || '',
              chartType: widget.chartType || 'horizontalBar',
              data: widget.data,
            };
          }
          console.log(`  ⚠️ ${widgetKey}: No data`);
          return { title: '', chartType: 'horizontalBar', data: [] };
        };
        
        state.dashboardCards = {
          // ROW 1
          adherenceToProductObjectives: extractWidgetData('adherenceToProductObjectives'),
          quotasAtRisk: extractWidgetData('quotasAtRisk'),
          quotasPendingSubmission: extractWidgetData('quotasPendingSubmission'),
          quotasPendingApproval: extractWidgetData('quotasPendingApproval'),
          // ROW 2
          topCountriesHighestDemand: extractWidgetData('topCountriesHighestDemand'),
          topCountriesMostExcess: extractWidgetData('topCountriesMostExcess'),
          topQuotaBreachesVsInitial: extractWidgetData('topQuotaBreachesVsInitial'),
          topProductExcessGrowth: extractWidgetData('topProductExcessGrowth'),
        };
        
        // Only set loading to false AFTER data is processed
        state.isLoadingDashboardCards = false;
        console.log("✅ [REDUX] Dashboard cards processed, loading set to false");
      })
      .addCase(getDashboardCards.rejected, (state, action) => {
        const errorMsg = action.payload || action.error?.message || "Unknown error";
        console.error("❌ [REDUX] getDashboardCards.rejected:", errorMsg);
        
        // Check if it's a CORS or network error
        if (errorMsg.includes("Failed to fetch") || errorMsg.includes("NetworkError")) {
          console.error("🔴 Network/CORS Error - Backend might not be running or CORS is blocking the request");
        }
        
        state.isLoadingDashboardCards = false;
        state.dashboardCardsError = errorMsg; // Store error for UI display
      })
      .addCase(getUserChatBotHistory.pending, (state, action) => {
        // No loading state needed for history
      })
      .addCase(getUserChatBotHistory.fulfilled, (state, action) => {
        if (action.payload && Array.isArray(action.payload) && action.payload.length > 0) {
          // Transform API response to chatHistory format
          const historyItems = action.payload.map((item, index) => ({
            id: item.ConversationId || item.conversationId || `chat-${Date.now()}-${index}`,
            title: item.Heading || item.heading || 'Untitled Chat',
            timestamp: item.CreatedDate || item.createdDate || new Date().toISOString(), // Use API timestamp if available
            conversationId: item.ConversationId || item.conversationId,
            chatId: item.ChatId || item.chatId || null, // Store ChatID from database
          }));
          
          // Sort history items by ChatID (highest first) - ChatID represents most recent conversation
          historyItems.sort((a, b) => {
            const chatIdA = a.chatId || 0;
            const chatIdB = b.chatId || 0;
            // Higher ChatID = more recent = should come first
            return chatIdB - chatIdA;
          });
          
          // Get existing chats that are not in the fetched history (newly created chats without ChatID yet)
          const existingChatIds = new Set(historyItems.map(item => item.id));
          const existingConversationIds = new Set(historyItems.map(item => item.conversationId).filter(Boolean));
          const newChats = state.chatHistory.filter(chat => 
            !existingChatIds.has(chat.id) && 
            !existingConversationIds.has(chat.conversationId) &&
            chat.conversationId !== null
          );
          
          // Add "New Chat" option at the beginning if not already present
          const hasNewChat = state.chatHistory.some(chat => chat.id === 'new-chat' || (chat.title === 'New Chat' && !chat.conversationId));
          const newChatEntry = hasNewChat 
            ? state.chatHistory.find(chat => chat.id === 'new-chat' || (chat.title === 'New Chat' && !chat.conversationId))
            : { 
                id: 'new-chat', 
                title: 'New Chat', 
                timestamp: new Date().toISOString(), 
                conversationId: null,
                chatId: null
              };
          
          // Sort new chats by timestamp (newest first) - these don't have ChatID yet
          newChats.sort((a, b) => {
            const timeA = new Date(a.timestamp || 0).getTime();
            const timeB = new Date(b.timestamp || 0).getTime();
            return timeB - timeA;
          });
          
          // Combine: "New Chat" first, then new chats (no ChatID), then history (sorted by ChatID descending)
          state.chatHistory = [
            newChatEntry,
            ...newChats,
            ...historyItems
          ].filter(Boolean);
        }
      })
      .addCase(getUserChatBotHistory.rejected, (state, action) => {
        console.error("Error from getUserChatBotHistory", action.error?.message);
        // Don't update state on error, keep existing history
      })
      .addCase(getMostAskedQuestions.pending, (state, action) => {
        state.isLoadingMostAskedQuestions = true;
      })
      .addCase(getMostAskedQuestions.fulfilled, (state, action) => {
        state.isLoadingMostAskedQuestions = false;
        state.mostAskedQuestions = action.payload || [];
      })
      .addCase(getMostAskedQuestions.rejected, (state, action) => {
        state.isLoadingMostAskedQuestions = false;
        // Fallback to default questions on error
        state.mostAskedQuestions = [
          "What tables are there and how are they connected? Give me a short summary.",
          "Which distribution center has the highest chance of being a bottleneck?",
          "What was the demand for our products by week in 2024?"
        ];
        console.error("Error from getMostAskedQuestions", action.error?.message);
      })
      .addCase(getFrequentlyAskedQuestions.pending, (state, action) => {
        state.isLoadingMostAskedQuestions = true;
      })
      .addCase(getFrequentlyAskedQuestions.fulfilled, (state, action) => {
        state.isLoadingMostAskedQuestions = false;
        // Use frequently asked questions from user data if available
        if (action.payload && Array.isArray(action.payload) && action.payload.length > 0) {
          state.mostAskedQuestions = action.payload;
        } else {
          // If no questions returned, keep existing or trigger fallback
          // The component will handle fallback to getMostAskedQuestions
        }
      })
      .addCase(getFrequentlyAskedQuestions.rejected, (state, action) => {
        state.isLoadingMostAskedQuestions = false;
        // Don't override existing questions on error, keep what we have
        // The component will handle fallback to getMostAskedQuestions
        console.error("Error from getFrequentlyAskedQuestions", action.error?.message);
      });
  },
});

export const {
  setState,
  addUserMessage,
  addBotMessage,
  addErrorMessage,
  clearMessages,
  setCurrentChat,
  addNewChat,
  updateChatTitle,
  deleteChat,
  clearError,
  clearFollowupQuestions,
} = chatbotSlice.actions;

export default chatbotSlice.reducer;

